import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { AutoGrowTextarea, ErrorBanner, SortTh, lower, rowClassName, sortRows, useGridSort } from './Configuration';
import { displayDate, toDateInput, todayDateInput } from './registerFormat';
import { SearchBox, anyFilterSet } from './registerFilters';
import { PROJECT_EVENT_TYPE_PRESETS } from './ProjectEventForm';
import { useRegisterDirty } from '../unsavedChangesGuard';

// Plain text field, no multi-select: eventType is freeform (schema.prisma's ProjectEvent comment),
// so there's no fixed vocabulary to build a MultiSelect's options from -- search is enough.
export const BLANK_EVENT_FILTERS = { text: '' };

export function filterEvents(items, filters) {
  const needle = (filters.text || '').trim().toLowerCase();
  if (!needle) return items;
  return items.filter(
    (e) => lower(e.title).includes(needle) || lower(e.eventType).includes(needle) || lower(e.notes).includes(needle),
  );
}

export function isEventFiltered(filters) {
  // No array-valued filters on this register (eventType is freeform, not a MultiSelect) -- just text.
  return anyFilterSet(filters, []);
}

export const EVENT_SORT_VALUE = {
  startDate: (r) => toDateInput(r.startDate),
  title: (r) => lower(r.title),
  eventType: (r) => lower(r.eventType),
};

const BLANK_EVENT = () => ({ title: 'New event', eventType: 'Meeting', startDate: todayDateInput() });

// `row` is a live grid row (edited in place), not a separate draft.
function buildPayload(row) {
  return {
    title: row.title.trim(),
    eventType: row.eventType.trim(),
    startDate: toDateInput(row.startDate),
    endDate: toDateInput(row.endDate) || null,
    internalOnly: !!row.internalOnly,
    notes: (row.notes || '').trim() || null,
  };
}

/**
 * A project's Project Events log (2026-09-11) -- stakeholder meetings, blackout periods, decision
 * deadlines: dated markers with real project impact but no home on the Schedule, a register, or a
 * BPA session. Customer-visible by default, `internalOnly` rows excluded -- same per-row opt-out
 * RaaiddRegister uses, enforced by ProjectEventsService, not here.
 *
 * Cells edit in place; Add/Save live on the grid's own toolbar and a row's ✕ removes it.
 *
 * One tab of ProjectRegisters.js, which owns the page header and the project picker. Also rendered
 * by CustomerProjectRegisters.js for a customer contact with edit rights on this project (item 5,
 * 2026-09-12) -- `readOnly` collapses the toolbar and the grid's write affordances. (Creating an
 * event from the *calendar* reuses the shared `ProjectEventModal` in ProjectEventForm.js instead.)
 * `internalOnly` stays a staff-only toggle regardless of edit rights (ProjectEventsService rejects it
 * from an external caller too), so its checkbox is gated on `portal`, not `readOnly`.
 */
export default function ProjectEventsRegister({ projectId, onCoverageChange, readOnly = false, portal = false }) {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ ...BLANK_EVENT_FILTERS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [sort, toggleSort] = useGridSort('startDate');

  useRegisterDirty(dirtyIds.size > 0);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    apiFetch(`/project-events?projectId=${projectId}`)
      .then((rows) => {
        setItems(rows);
        onCoverageChange?.(rows.length > 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterEvents(items, filters), [items, filters]);
  const sorted = useMemo(
    () => sortRows(filtered, sort, (row, key) => EVENT_SORT_VALUE[key](row)),
    [filtered, sort],
  );

  function editCell(id, key, value) {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, [key]: value } : i)));
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  // Add and delete reload the grid from the server, which would discard other rows' unsaved edits,
  // so save them first -- same as Project Budgets' Add Line.
  async function flushDirty(skipId) {
    const ids = [...dirtyIds].filter((id) => id !== skipId);
    if (!ids.length) return;
    await Promise.all(ids.map((id) => apiFetch(`/project-events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(buildPayload(items.find((r) => r.id === id))),
    })));
    setDirtyIds((prev) => { const next = new Set(prev); ids.forEach((id) => next.delete(id)); return next; });
  }

  async function addItem() {
    setBusy('adding');
    setActionError('');
    try {
      await flushDirty();
      await apiFetch('/project-events', { method: 'POST', body: JSON.stringify({ ...BLANK_EVENT(), projectId }) });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function saveAll() {
    setBusy('saving');
    setActionError('');
    try {
      await flushDirty();
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function deleteItem(id) {
    setActionError('');
    try {
      await flushDirty(id);
      await apiFetch(`/project-events/${id}`, { method: 'DELETE' });
      // A dirty edit on this row is now moot -- left in place, saveAll would later try to PATCH an
      // id that no longer exists and throw building the request, taking every other pending edit
      // down with it.
      setDirtyIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  return (
    <div>
      <ErrorBanner error={error} />
      {actionError && <ErrorBanner error={actionError} />}

      <div className="card" style={{ marginBottom: 16 }}>
        <SearchBox
          value={filters.text}
          onChange={(text) => setFilters({ text })}
          placeholder="Title, type, notes…"
        />
      </div>

      <div className="card">
        {!readOnly && (
          <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
            <div className="spacer" />
            <button className="btn btn-outline" onClick={addItem} disabled={!!busy}>
              {busy === 'adding' ? 'Adding…' : '+ Add event'}
            </button>
            <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyIds.size}>
              {busy === 'saving' ? 'Saving…' : dirtyIds.size ? `Save ${dirtyIds.size} change(s)` : 'Saved'}
            </button>
          </div>
        )}
        {loading ? (
          <div style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
        ) : !items.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">📅</div>
            <div className="placeholder-title">No project events yet</div>
            <div className="placeholder-sub">
              {readOnly
                ? 'Nothing has been logged on the project calendar yet.'
                : 'Log a stakeholder meeting, blackout period or decision deadline above — it shows on the Project Calendar too.'}
            </div>
          </div>
        ) : !sorted.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">🔍</div>
            <div className="placeholder-title">No events match this search</div>
            <div className="placeholder-sub">{items.length} logged against this project — clear the search to see them.</div>
          </div>
        ) : (
          <EventGrid
            items={sorted}
            sort={sort}
            onSort={toggleSort}
            dirtyIds={dirtyIds}
            onEdit={editCell}
            onDelete={deleteItem}
            readOnly={readOnly}
            portal={portal}
          />
        )}
      </div>
    </div>
  );
}

/** Exported: the customer portal renders the identical grid with `readOnly`. */
export function EventGrid({ items, sort, onSort, dirtyIds, onEdit, onDelete, readOnly, portal = false }) {
  const [confirmId, setConfirmId] = useState('');

  return (
    <div className="sheet-wrap">
      <table className="sheet">
        <thead>
          <tr>
            <SortTh label="Start" sortKey="startDate" sort={sort} onSort={onSort} style={{ width: 120 }} />
            <SortTh label="Title" sortKey="title" sort={sort} onSort={onSort} style={{ minWidth: 200 }} />
            <SortTh label="Type" sortKey="eventType" sort={sort} onSort={onSort} style={{ width: 160 }} />
            <th style={{ width: 120 }}>End</th>
            <th style={{ minWidth: 200 }}>Notes</th>
            <th style={{ width: 70 }}>Internal</th>
            <th style={{ width: readOnly ? 12 : 36 }} />
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.id} className={rowClassName(i, dirtyIds?.has(item.id))}>
              {readOnly ? (
                <td className="cell-read">{displayDate(item.startDate)}</td>
              ) : (
                <td><input type="date" className="cell-input" value={toDateInput(item.startDate)} onChange={(e) => onEdit(item.id, 'startDate', e.target.value)} /></td>
              )}
              {readOnly ? (
                <td className="cell-read">{item.title}</td>
              ) : (
                <td><input className="cell-input" value={item.title} onChange={(e) => onEdit(item.id, 'title', e.target.value)} /></td>
              )}
              {readOnly ? (
                <td className="cell-read">{item.eventType}</td>
              ) : (
                <td>
                  <input className="cell-input" list="project-event-type-presets-register" value={item.eventType} onChange={(e) => onEdit(item.id, 'eventType', e.target.value)} />
                  <datalist id="project-event-type-presets-register">
                    {PROJECT_EVENT_TYPE_PRESETS.map((t) => <option key={t} value={t} />)}
                  </datalist>
                </td>
              )}
              {readOnly ? (
                <td className="cell-read">{item.endDate ? displayDate(item.endDate) : '—'}</td>
              ) : (
                <td><input type="date" className="cell-input" value={toDateInput(item.endDate)} onChange={(e) => onEdit(item.id, 'endDate', e.target.value)} /></td>
              )}
              {readOnly ? (
                <td className="cell-read" style={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>{item.notes || '—'}</td>
              ) : (
                <td><AutoGrowTextarea value={item.notes || ''} onChange={(e) => onEdit(item.id, 'notes', e.target.value)} /></td>
              )}
              <td className="cell-check">
                {/* Staff-only regardless of `readOnly`: ProjectEventsService rejects an external
                    caller setting this too, so the checkbox is gated on `portal`. */}
                {portal ? (
                  item.internalOnly && <span className="badge badge-violet" title="Hidden from the customer portal">Internal</span>
                ) : (
                  <input
                    type="checkbox" checked={!!item.internalOnly} title="Hide this event from the customer portal"
                    onChange={(e) => onEdit(item.id, 'internalOnly', e.target.checked)}
                  />
                )}
              </td>
              <td className="cell-check">
                {!readOnly && (
                  confirmId === item.id ? (
                    <button className="icon-btn" onClick={() => { setConfirmId(''); onDelete(item.id); }} title="Confirm delete — can't be undone">✓?</button>
                  ) : (
                    <button className="icon-btn" onClick={() => setConfirmId(item.id)} title="Delete this event">✕</button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
